import { AI_PURCHASE_ADOPTION_THRESHOLDS } from '../../../constants/api.constants';

/**
 * Formats an adoption percentage for display.
 *
 * Per the plan doc's denominator edge-case note (§4.1): the backend always sends `0`
 * for a percentage when its denominator is `0` (e.g. no purchases/line items in range
 * at all), rather than `NaN`. The frontend renders that as "—" (not "0%") when the
 * denominator itself is also `0`, since "0% out of 0" is a meaningless signal that
 * would otherwise look identical to a genuine 0% adoption rate on real volume.
 */
export function formatAdoptionPercent(percent: number, denominator: number): string {
    if (denominator === 0) return '—';
    return `${percent.toFixed(percent % 1 === 0 ? 0 : 2)}%`;
}

export function formatCount(count: number): string {
    return count.toLocaleString('en-IN');
}

/**
 * Returns Tailwind classes for a percentage badge, colour-coded by adoption level.
 * Thresholds are a suggestion from the plan doc (§7.3): red <30%, amber 30-70%, green >70%.
 */
export function getAdoptionBadgeClasses(percent: number, denominator: number): string {
    if (denominator === 0) {
        return 'bg-[#f8fafc] border-[#e2e8f0] text-[#64748b]';
    }
    if (percent < AI_PURCHASE_ADOPTION_THRESHOLDS.LOW) {
        return 'bg-[#fef2f2] border-[#fecaca] text-[#dc2626]';
    }
    if (percent < AI_PURCHASE_ADOPTION_THRESHOLDS.MEDIUM) {
        return 'bg-[#fffbeb] border-[#fde68a] text-[#92400e]';
    }
    return 'bg-[#f0fdf4] border-[#bbf7d0] text-[#15803d]';
}

export function getAdoptionBarClasses(percent: number, denominator: number): string {
    if (denominator === 0) return 'bg-[#cbd5e1]';
    if (percent < AI_PURCHASE_ADOPTION_THRESHOLDS.LOW) return 'bg-[#ef4444]';
    if (percent < AI_PURCHASE_ADOPTION_THRESHOLDS.MEDIUM) return 'bg-[#f59e0b]';
    return 'bg-[#22c55e]';
}

/**
 * Short accent label for the adoption-level badge shown on top of the bright hero
 * card backgrounds (overall summary / per-state tiles) — same red/amber/green
 * thresholds as `getAdoptionBadgeClasses`/`getAdoptionBarClasses`, just as a label.
 */
export function getAdoptionLevelLabel(percent: number, denominator: number): string {
    if (denominator === 0) return 'No data';
    if (percent < AI_PURCHASE_ADOPTION_THRESHOLDS.LOW) return 'Low';
    if (percent < AI_PURCHASE_ADOPTION_THRESHOLDS.MEDIUM) return 'Medium';
    return 'High';
}
